// @ts-strict-ignore
import React from 'react';
import { Box } from '@usealloy/component-library';
import {
  getActionName,
  MatrixAxisDataType,
} from 'flow/service/actions';
import type {
  DataRoute,
  MatrixModel,
  SchemaInstance,
} from 'flow/store/engine/types';
import { StyledTable, StyledText } from './styles';
import { formatValueIfUnbounded } from '~/dashboard/views/workflows/service/actions/matrixModels/formatters';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';

export const getMatrixColumns = ({
  xAxisHeader,
  xAxisHeaderDataType,
  yDataRouteText,
}: {
  xAxisHeader: MatrixModel['axis']['x']['axisHeader'];
  xAxisHeaderDataType: MatrixModel['axis']['x']['axisHeaderDataType'];
  yDataRouteText: string;
}) => {
  const xAxisHeaderDisplay = xAxisHeaderDataType === MatrixAxisDataType.STRING
    ? xAxisHeader.map((value, index) => ({
      id: `${value} - ${index}`,
      header: value,
      accessorKey: String(index),
    }))
    : xAxisHeader.slice(0, -1).map((value, index) => ({
      id: `${value} - ${xAxisHeader[index + 1]}`,
      header: `${formatValueIfUnbounded(
        Number(value),
      )} - ${formatValueIfUnbounded(
        Number(xAxisHeader[index + 1]),
      )}`,
      accessorKey: String(index),
    }));

  if (!xAxisHeaderDisplay?.length) {
    xAxisHeaderDisplay.push({
      id: '1D matrix cell values',
      header: '',
      accessorKey: '0',
    });
  }

  return [
    {
      id: 'yAxis',
      header: (
        <Box justifyContent="flex-start">
          Y Axis: {yDataRouteText}
        </Box>
      ),
      accessorKey: 'yAxis',
    },
    ...xAxisHeaderDisplay,
  ];
};

export const getMatrixRows = ({ matrix, yAxisHeader, yAxisHeaderDataType }: {
  matrix: MatrixModel['matrix'];
  yAxisHeader: MatrixModel['axis']['y']['axisHeader'];
  yAxisHeaderDataType: MatrixModel['axis']['y']['axisHeaderDataType'];
}) => matrix.map((row, index) => {
  const yAxisHeaderDisplay = yAxisHeaderDataType === MatrixAxisDataType.STRING
    ? yAxisHeader[index]
    : `${formatValueIfUnbounded(
      Number(yAxisHeader[index]),
    )} - ${formatValueIfUnbounded(
      Number(yAxisHeader[index + 1]),
    )}`;

  return {
    yAxis: yAxisHeaderDisplay,
    ...row.map(cell => cell),
  };
});

export const buildReadOnlyMatrix = ({
  is1Dimensional, xDataRouteText, columns, rows: data,
}: {
  is1Dimensional: boolean;
  xDataRouteText: string;
  columns: ReturnType<typeof getMatrixColumns>;
  rows: ReturnType<typeof getMatrixRows>;
}) => (
  <Box
    alignItems="flex-start"
    direction="column"
    justifyContent="flex-start"
    gap={2}
    width="auto"
  >
    {!is1Dimensional && (
      <Box justifyContent="flex-start" style={{ paddingLeft: '310px' }}>
        <StyledText>
          X Axis: {xDataRouteText}
        </StyledText>
      </Box>
    )}
    <StyledTable is1Dimensional={is1Dimensional}>
      <thead>
        <tr>
          {columns.map(column => (
            <th key={column.accessorKey}>{column.header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map(oneData => (
          <tr key={JSON.stringify(oneData)}>
            {columns.map(column => (
              <td key={oneData[column.accessorKey]}>{oneData[column.accessorKey]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </StyledTable>
  </Box>
);


function getAttributeString({
  actions, dataRoute,
}: {
  actions: SchemaInstance['actions'];
  dataRoute: DataRoute;
}) {
  const { sourceId } = dataRoute;
  const dataRouteAttribute = dataRoute.attribute;
  const source = actions[sourceId];
  if (source.type === ActionType.SERVICE) {
    const { service: { name } } = source;
    return `${name}: ${dataRouteAttribute.displayName}`;
  }
  return getActionName(source);
}

export function getDataRouteTarget({
  actions, dataRoute,
}: {
  actions: SchemaInstance['actions'];
  dataRoute: DataRoute;
}) {
  const { attribute } = dataRoute;
  if (attribute.multiLevelAttributeFilter) {
    return getAttributeString({
      actions,
      dataRoute,
    });
  }
  const actionName = getActionName(actions[dataRoute.sourceId]);
  const secondaryDescriptor = dataRoute.category.name
    || dataRoute.attribute.attributeName
    || '';
  return `${actionName}${secondaryDescriptor && ` ${secondaryDescriptor}`}`;
}

